import { FileTextIcon, MessagesSquareIcon, TablePropertiesIcon } from "lucide-react";
import { adaptContent, Message as SharedMessage } from "@promptlayer/ui";

import type { TraceTranscriptStats } from "@/components/RequestDisplay/ChatView/utils";
import { Badge, BadgeSize, BadgeVariant } from "@/components/ui/Badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

export const RequestDisplayModeSwitcher = ({
  chatStats,
  systemMessage,
  isChatView,
  onSelectView,
}: {
  chatStats?: TraceTranscriptStats;
  systemMessage?: string;
  isChatView: boolean;
  onSelectView: (view: "template" | "chat") => void;
}) => (
  <div className="flex w-full items-center justify-between gap-3">
    <Tabs
      value={isChatView ? "chat" : "template"}
      onValueChange={value => {
        if (value === "chat" || value === "template") {
          onSelectView(value);
        }
      }}
    >
      <TabsList className="h-auto rounded-xl border border-gray-200 bg-gray-50/90 p-0.5 shadow-sm">
        <TabsTrigger
          value="chat"
          className={cn(
            "gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-gray-500 transition-colors",
            "focus-visible:ring-1 focus-visible:ring-gray-300 focus-visible:ring-offset-0",
            "data-[state=active]:text-gray-900",
          )}
        >
          <MessagesSquareIcon className="h-3.5 w-3.5" />
          <span>Chat</span>
        </TabsTrigger>
        <TabsTrigger
          value="template"
          className={cn(
            "gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-gray-500 transition-colors",
            "focus-visible:ring-1 focus-visible:ring-gray-300 focus-visible:ring-offset-0",
            "data-[state=active]:text-gray-900",
          )}
        >
          <TablePropertiesIcon className="h-3.5 w-3.5" />
          <span>Template</span>
        </TabsTrigger>
      </TabsList>
    </Tabs>
    <div className="flex items-center gap-2">
      {chatStats ? (
        <>
          <Badge variant={BadgeVariant.GRAY} size={BadgeSize.SM}>
            {chatStats.turnCount} Turn{chatStats.turnCount === 1 ? "" : "s"}
          </Badge>
          <Badge variant={BadgeVariant.GRAY} size={BadgeSize.SM}>
            {chatStats.toolCallCount} Tool Call{chatStats.toolCallCount === 1 ? "" : "s"}
          </Badge>
        </>
      ) : null}
      {systemMessage && isChatView ? (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-600 shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-900"
            >
              <FileTextIcon className="h-3.5 w-3.5" />
              <span>System Message</span>
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[564px] max-w-[90vw] overflow-x-hidden p-0">
            <div className="border-b border-gray-200 px-4 py-3">
              <h4 className="text-sm font-medium text-gray-900">System Message</h4>
              <p className="mt-1 text-xs text-gray-500">
                Hidden from the chat transcript, available here for reference.
              </p>
            </div>
            <div className="max-h-[360px] overflow-y-auto overflow-x-hidden py-4 pr-8">
              <SharedMessage
                id="request-display-system-message"
                role="assistant"
                content={adaptContent([{ type: "text", text: systemMessage }])}
                bubbleClassName="max-w-full overflow-x-hidden border border-slate-200 bg-slate-50 text-slate-700"
                maxWidthClass="max-w-full"
                hideCopyButton={false}
                disableAnimation
              />
            </div>
          </PopoverContent>
        </Popover>
      ) : null}
    </div>
  </div>
);
